import useOffer from 'hooks/Offers/useOffer'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import PageBanner from 'LuxKit/Banners/PageBanner'
import React, { useCallback, useMemo, useState } from 'react'
import { matchPath, useRouteMatch } from 'react-router'
import styled from 'styled-components'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import BodyText from 'LuxKit/Typography/BodyText'
import LineMultiplyIcon from 'LuxKit/Icons/line/LineMultiplyIcon'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import TextButton from 'LuxKit/Button/TextButton'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const SiteTakeoverBackgroundStyle = styled.div<{ backgroundStyle?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: ${props => props.backgroundStyle};
`

const Content = styled.div`
  transform: translateZ(0);
`

const Banner = styled.div`
  position: relative;
`

const BannerContainer = styled(Banner)`
  display: flex;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(12)};
  align-items: center;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(24)};
    gap: 0;
  }
`

const MobileBannerContent = styled(Content)`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex: 1;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const MobileDismissButton = styled.div`
  padding: ${rem(4)};
  border-radius: ${rem(2)};
  cursor: pointer;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DismissButton = styled(Content)`
  display: none;

  ${mediaQueryUp.desktop} {
    border-radius: ${rem(2)};
    display: flex;
    padding: ${rem(4)};
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};
    cursor: pointer;
  }
`

const MobileTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const MobileSubtitleRow = styled.div`
  display: flex;
  gap: ${rem(8)};
`

const TextContentWrapper = styled.div`
  flex: 1;
`

const BannerContent = styled(Content)`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    padding: ${rem(8)} ${rem(150)};
    justify-content: center;
    align-items: center;
    gap: ${rem(20)};
    flex: 1 0 0;
  }
`

const BannerIconAndText = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

const BannerActionsGroup = styled(Content)`
  ${mediaQueryUp.desktop} {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${rem(8)};
    flex-shrink: 0;
  }
`

const StyledActionButton = styled(TextButton)`
  border-radius: 2px;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background: ${props => props.theme.palette.neutral.default.eight};
  
  &:hover {
    background: ${props => props.theme.palette.neutral.default.seven};
  }
`

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/cruises/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
  '/:regionCode/tour/:offerId',
  '/:regionCode/partner/:slug/:offerId',
]

const noBannerRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/checkout',
  '/:regionCode/account',
  '/:regionCode/booking',
  '/:regionCode/bb/booking',
  '/:regionCode/trip-planner/generate-trip',
]

const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

const invalidExactRoutes: Array<string> = [
  '/:regionCode',
  '/:regionCode/triple-points',
]

const homePageRoutes: Array<string> = [
  '/:regionCode',
]

function SiteTakeoverPageBanner() {
  const match = useRouteMatch<{ offerId: string }>(offerPageRoutes)
  const [offer] = useOffer(match?.params.offerId)
  const currentPath = useAppSelector(state => state.router.location.pathname)

  // Call all hooks unconditionally
  const siteTakeoverV2 = useCurrentSiteTakeoverV2()
  const siteTakeover = siteTakeoverV2

  const siteTakeoverOnTourV2 = useCurrentSiteTakeoverV2({ heroVertical: 'tours' })
  const siteTakeoverOnTour = siteTakeoverOnTourV2

  const showingOfferBanner = !!(offer && siteTakeover?.offerTypes?.has(offer.type))
  const [isDismissed, setIsDismissed] = useState(false)
  const labSiteTakeoverTheme = useSiteTakeoverTheme()

  const onDismissHandler = useCallback(() => {
    setIsDismissed(true)
  }, [])

  const hide = useMemo((): boolean => {
    const exceptedRoutes: Array<string> = siteTakeover?.siteWide?.banner.exceptedRoutes ?? EmptyArray
    // hide on:
    // - The home page (has its own banner)
    // - Search page results (has it's own banner)
    // - Would show the offer page banner  (has it's own banner)
    // - Any of the other no banner routes (shouldn't show banners)
    // - When tour has it's own takeover
    return showingOfferBanner ||
      !!matchPath(currentPath, { path: homePageRoutes, exact: true }) ||
      !!matchPath(currentPath, { path: invalidExactRoutes, exact: true }) ||
      (!!exceptedRoutes.length && !!matchPath(currentPath, { path: exceptedRoutes })) ||
      !!matchPath(currentPath, { path: noBannerRoutes }) ||
      (!!siteTakeoverOnTour && !!matchPath(currentPath, { path: toursRoutes }))
  }, [currentPath, showingOfferBanner, siteTakeover, siteTakeoverOnTour])

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const useLab = !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!siteTakeover

  const shouldHideOnMobile = useMemo(() => {
    return !!siteTakeover?.siteWide?.banner?.hideOnOfferPagesForMobileScreenSize &&
      !!matchPath(currentPath, { path: offerPageRoutes })
  }, [siteTakeover, currentPath])

  if (hide || isDismissed) {
    return null
  }

  if (useCustom && siteTakeover?.siteWide?.banner) {
    return (
      <CSSBreakpoint min={shouldHideOnMobile ? 'tablet' : undefined}>
        <Banner>
          <Background background="default" />
          <Content className={getThemeClassName({ mobile: siteTakeover.palette })}>
            <PageBanner
              primaryActionButtonProps={siteTakeover.siteWide.banner.bannerAction}
              density="compact"
              icon={siteTakeover.siteWide.banner.logo}
              align={siteTakeover.siteWide.banner.align ?? 'center'}
              kind="none"
            >
              {siteTakeover.siteWide.banner.description}
            </PageBanner>
          </Content>
        </Banner>
      </CSSBreakpoint>
    )
  }

  if (useLab && siteTakeoverBannerContentPayload) {
    // Extract values from theme and payload
    const title = siteTakeoverBannerContentPayload?.title
    const subtitle = siteTakeoverBannerContentPayload?.subtitle
    const ctaLink = siteTakeoverBannerContentPayload?.ctaLink
    const isDismissible = siteTakeoverBannerContentPayload?.isDismissible
    const logoRef = labSiteTakeoverTheme?.logoRef
    const backgroundStyle = labSiteTakeoverTheme?.backgroundStyle

    return (
      <BannerContainer>
        {backgroundStyle && <SiteTakeoverBackgroundStyle backgroundStyle={backgroundStyle} />}

        <MobileBannerContent>
          {logoRef && <Image image={{ id: logoRef }} dpr={2} width={32} height={32} />}
          <TextContentWrapper>
            <MobileTitleRow>
              <BodyText weight="bold" variant="medium">{title}</BodyText>
            </MobileTitleRow>
            <MobileSubtitleRow>
              <BodyText variant="small">{subtitle}</BodyText>
              {ctaLink && (
                <TextLink to={ctaLink} weight="bold" size="small">View all</TextLink>
              )}
            </MobileSubtitleRow>
          </TextContentWrapper>
          {isDismissible && (
            <MobileDismissButton onClick={onDismissHandler}>
              <LineMultiplyIcon size="S" />
            </MobileDismissButton>
          )}
        </MobileBannerContent>

        <BannerContent>
          <BannerIconAndText>
            {logoRef && <Image image={{ id: logoRef }} dpr={2} width={32} height={32} />}
            <BodyText weight="bold" variant="medium">{title}</BodyText>
            <BodyText variant="medium">{subtitle}</BodyText>
          </BannerIconAndText>
          {ctaLink && (
            <BannerActionsGroup>
              <StyledActionButton kind="secondary" to={ctaLink} size="small">
                View offers
              </StyledActionButton>
            </BannerActionsGroup>
          )}
        </BannerContent>
        {isDismissible && (
          <DismissButton onClick={onDismissHandler}>
            <LineMultiplyIcon size="S" />
          </DismissButton>
        )}
      </BannerContainer>
    )
  }

  return null
}

export default SiteTakeoverPageBanner
